import React from "react";
import { Link } from "react-router-dom";
import { CHAPTERS } from "../lib/chapters";
import { PATHOLOGY_TO_CHAPTERS, CHAPTER_TO_PATHOLOGIES } from "../lib/researchLinks";
import { PATHOLOGIES } from "../lib/pathologies";

/**
 * Block displayed at the bottom of a /pathologie/:slug page,
 * linking to the academic chapters that cover the same condition.
 */
export const ChaptersForPathology = ({ pathologySlug, lang = "fr" }) => {
  const chapterIds = PATHOLOGY_TO_CHAPTERS[pathologySlug] || [];
  if (chapterIds.length === 0) return null;
  const chapters = chapterIds
    .map((id) => CHAPTERS.find((c) => c.id === id))
    .filter(Boolean);
  if (chapters.length === 0) return null;

  return (
    <section
      data-testid="chapters-for-pathology"
      className="mt-16 rounded-2xl border-2 border-dashed border-[#FF5A1F]/30 bg-gradient-to-br from-[#FFF9F4] to-white p-7 md:p-9"
    >
      <div className="flex items-center gap-3">
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center text-white font-display font-black"
          style={{ backgroundColor: "#0A192F" }}
        >
          <i className="fa-solid fa-book-open" aria-hidden="true"></i>
        </div>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF5A1F]">
            {lang === "fr" ? "Aller plus loin" : "Go deeper"}
          </p>
          <h2 className="font-display text-2xl font-bold text-navy">
            {lang === "fr"
              ? "Chapitres académiques liés"
              : "Related academic chapters"}
          </h2>
        </div>
      </div>
      <p className="mt-3 text-sm text-foreground/70 max-w-2xl">
        {lang === "fr"
          ? "Synthèses scientifiques rédigées par le Dr Aswad sur cette pathologie. Niveau spécialisé : pour patients curieux, médecins, kinésithérapeutes et étudiants."
          : "Scientific syntheses written by Dr Aswad on this condition. Specialist level: for curious patients, physicians, physiotherapists and students."}
      </p>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        {chapters.map((c) => (
          <Link
            key={c.id}
            to={`/recherche/chapitre-${c.id}-${c.slug}`}
            data-testid={`chapter-link-${c.id}`}
            className="group rounded-xl bg-white border border-border p-5 hover:shadow-md hover:border-[#FF5A1F]/40 transition-all"
            style={{ borderTopWidth: 4, borderTopColor: c.color }}
          >
            <p
              className="text-[10px] font-bold uppercase tracking-[0.18em]"
              style={{ color: c.color }}
            >
              {lang === "fr" ? `Chapitre ${c.id}` : `Chapter ${c.id}`} · {c.readingMin || 15} min
            </p>
            <p className="mt-2 font-display font-bold text-navy group-hover:text-[#FF5A1F] transition-colors">
              {c.title}
            </p>
            <p className="mt-2 text-xs text-muted-foreground line-clamp-2">
              {c.subtitle}
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#FF5A1F]">
              {lang === "fr" ? "Lire le chapitre" : "Read chapter"}
              <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
};

/**
 * Block displayed at the top of a /recherche/:slug chapter page,
 * linking to the clinical pathology pages that the chapter covers.
 */
export const PathologiesForChapter = ({ chapterId, lang = "fr" }) => {
  const slugs = CHAPTER_TO_PATHOLOGIES[chapterId] || [];
  if (slugs.length === 0) return null;

  // Resolve pathology slugs to their full data (across all regions)
  const items = [];
  for (const slug of slugs) {
    for (const region of Object.values(PATHOLOGIES)) {
      const found = region.items.find((it) => it.slug === slug);
      if (found) {
        items.push({ ...found, region_fr: region.title_fr, region_en: region.title_en });
        break;
      }
    }
  }
  if (items.length === 0) return null;

  return (
    <aside
      data-testid="pathologies-for-chapter"
      className="mt-10 rounded-2xl border border-border bg-[#F8FAFC] p-6 md:p-7 print:hidden"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
        {lang === "fr"
          ? "Pour le patient — pathologies cliniques"
          : "For patients — clinical conditions"}
      </p>
      <p className="mt-1 text-sm text-foreground/75">
        {lang === "fr"
          ? "Vous cherchez l'information patient associée à ce chapitre ? Consultez les fiches cliniques :"
          : "Looking for patient information related to this chapter? See the clinical pages:"}
      </p>
      <div className="mt-4 flex flex-wrap gap-2">
        {items.map((it) => (
          <Link
            key={it.slug}
            to={`/pathologie/${it.slug}`}
            data-testid={`pathology-link-${it.slug}`}
            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-border px-3 py-1.5 text-xs font-semibold text-navy hover:bg-[#FF5A1F] hover:text-white hover:border-[#FF5A1F] transition-all"
          >
            <i className="fa-solid fa-stethoscope text-[10px]" aria-hidden="true"></i>
            {lang === "fr" ? it.name_fr : it.name_en}
          </Link>
        ))}
      </div>
    </aside>
  );
};
